/**
 * Encrypted identity backup/restore for cross-device E2EE continuity.
 * Uses PBKDF2 + AES-256-GCM to encrypt the ratchet identity before
 * storing it server-side. Only the user's passphrase can unlock it.
 */

import { supabase } from '@/integrations/supabase/client';
import { getOrCreateIdentity, getIdentity } from './double-ratchet';
import { storeIdentity } from './ratchet-session-store';
import { arrayBufferToBase64, base64ToArrayBuffer } from './crypto-utils';
import type { Identity } from '2key-ratchet';
import type { IJsonIdentity } from '2key-ratchet';

const PBKDF2_ITERATIONS = 600_000;
const SALT_BYTES = 32;
const IV_BYTES = 12;

// ─── Key derivation ────────────────────────────────────────

async function deriveKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

// ─── Encrypt / Decrypt helpers ─────────────────────────────

async function encryptWithPassphrase(
  plaintext: string,
  passphrase: string,
): Promise<{ ciphertext: string; salt: string }> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const key = await deriveKey(passphrase, salt);

  const encoded = new TextEncoder().encode(plaintext);
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoded,
  );

  // Prepend IV to ciphertext
  const combined = new Uint8Array(IV_BYTES + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), IV_BYTES);

  return {
    ciphertext: arrayBufferToBase64(combined.buffer),
    salt: arrayBufferToBase64(salt.buffer),
  };
}

async function decryptWithPassphrase(
  ciphertextB64: string,
  saltB64: string,
  passphrase: string,
): Promise<string> {
  const combined = new Uint8Array(base64ToArrayBuffer(ciphertextB64));
  const salt = new Uint8Array(base64ToArrayBuffer(saltB64));

  const iv = combined.slice(0, IV_BYTES);
  const ciphertext = combined.slice(IV_BYTES);

  const key = await deriveKey(passphrase, salt);
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext,
  );

  return new TextDecoder().decode(decrypted);
}

// ─── Public API ────────────────────────────────────────────

/**
 * Backup the user's ratchet identity, encrypted with their passphrase.
 */
export async function backupIdentity(
  userId: string,
  passphrase: string,
): Promise<boolean> {
  try {
    const identity = await getIdentity(userId);
    if (!identity) {
      console.warn('[IdentityBackup] No identity to backup');
      return false;
    }

    const identityJson = await identity.toJSON();
    const plaintext = JSON.stringify(identityJson);
    const { ciphertext, salt } = await encryptWithPassphrase(plaintext, passphrase);

    const { error } = await supabase
      .from('encryption_identity_backup' as any)
      .upsert({
        user_id: userId,
        encrypted_identity: ciphertext,
        salt,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id' });

    if (error) {
      console.error('[IdentityBackup] Failed to store backup:', error);
      return false;
    }

    console.log('[IdentityBackup] Identity backed up successfully');
    return true;
  } catch (err) {
    console.error('[IdentityBackup] Backup failed:', err);
    return false;
  }
}

/**
 * Restore the user's ratchet identity from server backup using their passphrase.
 */
export async function restoreIdentity(
  userId: string,
  passphrase: string,
): Promise<Identity | null> {
  try {
    const { data, error } = await supabase
      .from('encryption_identity_backup' as any)
      .select('encrypted_identity, salt')
      .eq('user_id', userId)
      .single();

    if (error || !data) {
      console.warn('[IdentityBackup] No backup found');
      return null;
    }

    const plaintext = await decryptWithPassphrase(
      (data as any).encrypted_identity,
      (data as any).salt,
      passphrase,
    );

    const identityJson = JSON.parse(plaintext) as IJsonIdentity;
    const { Identity } = await import('2key-ratchet');
    const identity = await Identity.fromJSON(identityJson);

    // Store in IndexedDB for local use
    await storeIdentity(userId, identityJson);

    console.log('[IdentityBackup] Identity restored successfully');
    return identity;
  } catch (err) {
    console.error('[IdentityBackup] Restore failed (wrong passphrase?):', err);
    return null;
  }
}

/**
 * Check if a backup exists for this user.
 */
export async function hasIdentityBackup(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('encryption_identity_backup' as any)
    .select('user_id')
    .eq('user_id', userId)
    .maybeSingle();

  return !error && !!data;
}
